import { useCallback, useMemo, useState } from 'react';

import { Box, SimpleGrid } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, DECIMAL_PROPS, MonthPicker, NumberInput, Select } from 'components';
import { CreditOptionsResponse, CreditResponse, IncomeType } from 'types';
import { AppUtils } from 'utils';

interface CreditRequestIncomeProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  partner: boolean;
  readOnly?: boolean;
}

interface IncomeObject {
  creditRequestId?: number;
  incomeType?: IncomeType;
  startDate?: Date;
  amount?: number;
  partner?: boolean;
}
const tempIncomeObject = {
  creditRequestId: undefined,
  incomeType: undefined,
  startDate: undefined,
  amount: undefined,
  partner: false,
} as IncomeObject;

const propertyName = 'incomes';

const CreditRequestIncome = ({ credit, form, options, partner, readOnly = false }: CreditRequestIncomeProps) => {
  const { t } = useTranslation();
  const [visibleFields, setVisibleFields] = useState<string[]>(
    AppUtils.getVisibleIncomeFields(credit[propertyName]),
  );

  const getInitialData = useCallback(() => {
    const formValues: IncomeObject[] | undefined = credit[propertyName];
    let array = [];
    if (!formValues || formValues.length === 0) {
      array.push(tempIncomeObject);
    } else {
      const filtered = formValues.filter((el) => el.partner === partner);
      if (filtered.length > 0) {
        array = filtered.map((el) => ({
          ...el,
          startDate: el.startDate ? new Date(el.startDate) : undefined,
        }));
      } else {
        array.push(tempIncomeObject);
      }
    }
    return array;
  }, []);

  const incomeForm = useForm({
    mode: 'uncontrolled',
    initialValues: getInitialData() as IncomeObject[],
    onValuesChange(values, previous) {
      if (values.length === 1 || values[values.length - 1] !== tempIncomeObject) {
        updateCredit(values, previous);
      }
    },
  });

  const updateCredit = useCallback(async (values: any, previous: any) => {
    const prevVisibleFields = AppUtils.getVisibleIncomeFields(previous);
    const newVisibleFields = AppUtils.getVisibleIncomeFields(values);
    AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
    AppUtils.updateArrayFieldsWhenVisibilityChanges(
      prevVisibleFields,
      newVisibleFields,
      values,
      incomeForm,
    );

    const formValues: IncomeObject[] | undefined = form.getValues()[propertyName];
    const tempArray: IncomeObject[] = formValues
      ? formValues.filter((el: IncomeObject) => el.partner !== partner)
      : [];
    Object.keys(values).forEach((key) => {
      const item: IncomeObject = { ...values[key] };
      const isNotEmpty = item.incomeType || item.startDate || item.amount;
      if (isNotEmpty) {
        item.creditRequestId = credit.id;
        item.partner = partner;
        tempArray.push(item);
      }
    });
    form.setFieldValue(propertyName, tempArray);
    setVisibleFields(newVisibleFields);
  }, []);

  const incomeView = useMemo(() => {
    const transformed = incomeForm.getTransformedValues();
    const length = Object.keys(transformed).length;

    return Object.keys(transformed).map((income, index: number) => (
      <Box
        key={`${income}-${index}`}
        style={[
          index > 0 ? { borderTop: '1px solid var(--mantine-color-gray-4)', marginTop: 16 } : {},
        ]}
      >
        <SimpleGrid cols={4} mt={'sm'}>
          <Select
            label={t(`credit.income.incomeType.label`)}
            data={options['incomeType']
              .filter((el) => form.values['maritalStatus'] !== 'MARRIED' || el !== 'WIDOWS_PENSION')
              .map((el) => ({ value: el, label: t(`credit.income.incomeType.options.${el}`) }))}
            key={incomeForm.key(`${index}.incomeType`)}
            {...incomeForm.getInputProps(`${index}.incomeType`)}
            clearable={true}
            disabled={readOnly}
          />
          <NumberInput
            label={t(`credit.income.amount.label`)}
            key={incomeForm.key(`${index}.amount`)}
            {...incomeForm.getInputProps(`${index}.amount`)}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
          {visibleFields.includes(`incomes.${index}.startDate`) && (
            <MonthPicker
              label={t(`credit.income.startDate.label`)}
              key={incomeForm.key(`${index}.startDate`)}
              {...incomeForm.getInputProps(`${index}.startDate`)}
              maxDate={new Date()}
              disabled={readOnly}
            />
          )}
          {length > 1 && !readOnly && (
            <Box style={{ textAlign: 'right' }}>
              <Button
                variant={'transparent'}
                size={'compact-sm'}
                color={'var(--mantine-color-error)'}
                mb={6}
                onClick={() => AppUtils.removeObject(incomeForm, index, tempIncomeObject)}
              >
                <IconTrash size={20} />
              </Button>
            </Box>
          )}
        </SimpleGrid>
      </Box>
    ));
  }, [incomeForm, t]);

  return (
    <Box p={'md'} pt={'xs'} mt={'sm'}>
      {incomeView}
      {!readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(incomeForm, tempIncomeObject)}
          size={'compact-xs'}
          variant={'transparent'}
          mt={'md'}
        >
          <>
            <Box mr={4}>
              <IconPlus size={16} />
            </Box>
            {t('credit.income.add')}
          </>
        </Button>
      )}
    </Box>
  );
};

export default CreditRequestIncome;
